import { Link } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { TopoContours } from '@/components/patterns'
import FundraisingProgressBar from '@/components/event-show/FundraisingProgressBar'
import { formatDressCode, formatTime } from '@/lib/format'
import {
  Calendar,
  Clock,
  MapPin,
  Tag,
  CommitteeSection,
  CountdownBlock,
  DetailIconRow,
  EntertainmentSection,
  EventNeedsSection,
  HashtagsSection,
  HonoreesSection,
  HostsSection,
  IndustriesSection,
  CausesSection,
  OrganizationCard,
  PurchaseActions,
  SaveEventButton,
  ShareAndCalendar,
  SponsorsSection,
  VenueMap,
  buildAddressQuery,
  buildLocationLines,
  formatDate,
  formatPrice,
  hasPurchaseActionContent,
  useCountdown,
} from './shared'
import type { DetailOrganization, Event } from '@/types'

type Props = {
  event: Event
  organization: DetailOrganization | null
  isFollowing: boolean
  isSaved: boolean
  googleMapsApiKey: string | null
  brandFree: boolean
  eventUrl: string
  isPreview: boolean
}

export default function ClassicTemplate({
  event,
  organization,
  isFollowing,
  isSaved,
  googleMapsApiKey,
  brandFree,
  eventUrl,
  isPreview,
}: Props) {
  const dressCode = formatDressCode(event.dress_code)
  const startTime = formatTime(event.start_time)
  const endTime = formatTime(event.end_time)
  const timeRange =
    startTime && endTime ? `${startTime} – ${endTime}` : startTime || endTime || null
  const eventDate = event.date ?? ''
  const countdown = useCountdown(eventDate, event.start_time)
  const showCountdown = event.countdown_timer && countdown !== null
  const hasImage = !!event.cover_image_url
  const locationLines = buildLocationLines(event)
  const addressQuery = buildAddressQuery(event)
  const showPurchaseCard = Boolean(event.starting_ticket_price) || hasPurchaseActionContent(event, organization)

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        {!brandFree && (
          <Link href="/" className="mb-4 inline-flex items-center gap-1.5 text-base text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-3.5" />
            Back to events
          </Link>
        )}
        {hasImage ? (
          <div className="relative h-72 w-full overflow-hidden rounded-2xl sm:h-96 lg:h-[28rem]">
            <img src={event.cover_image_url!} alt="" className="h-full w-full object-cover" />
          </div>
        ) : (
          <div className="relative h-72 w-full overflow-hidden rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E] sm:h-96 lg:h-[28rem]">
            <TopoContours />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
          </div>
        )}
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
        <FundraisingProgressBar event={event} />
      </div>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2 [&>*+*]:border-t [&>*+*]:border-black/5 [&>*+*]:pt-5">
            <div>
              <h1 className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl">{event.title}</h1>
              {organization && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-muted-foreground">
                  Hosted by{' '}
                  {brandFree ? (
                    <span className="font-medium text-foreground">{organization.name}</span>
                  ) : (
                    <Link href={`/o/${organization.slug}`} className="font-medium text-foreground underline-offset-4 hover:underline">
                      {organization.name}
                    </Link>
                  )}
                  {organization.verified && <VerifiedBadge className="size-4 text-foreground" />}
                </p>
              )}
            </div>
            {event.description && (
              <div>
                <h2 className="mb-4 text-xl font-semibold">About the Event</h2>
                <p className="max-w-[65ch] whitespace-pre-wrap text-pretty">{event.description}</p>
              </div>
            )}
            <HostsSection hosts={event.hosts} />
            <HonoreesSection honorees={event.honorees} />
            <EntertainmentSection entertainment={event.entertainment} />
            <SponsorsSection event={event} isPreview={isPreview} />
            <CommitteeSection members={event.committee_members} />
            <HashtagsSection hashtags={event.hashtags} />
            {organization && <CausesSection causes={organization.causes} />}
            {organization && <IndustriesSection industries={organization.industries} />}
            <VenueMap event={event} locationLines={locationLines} addressQuery={addressQuery} apiKey={googleMapsApiKey} />
            <EventNeedsSection event={event} />
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6 rounded-2xl bg-muted/30 p-6 ring-1 ring-black/5">
              <h3 className="text-lg font-semibold tracking-tight">Event Details</h3>

              {showCountdown && countdown && <CountdownBlock countdown={countdown} />}

              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <Calendar className="size-4 text-primary" />
                </div>
                  <p className="font-semibold">{formatDate(eventDate)}</p>
              </div>

              {timeRange && (
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <Clock className="size-4 text-primary" />
                  </div>
                  <p className="font-semibold">{timeRange}</p>
                </div>
              )}

              {locationLines.length > 0 && (
                <DetailIconRow icon={<MapPin className="size-4 text-primary" />}>
                  <p className="font-semibold">{locationLines[0]}</p>
                  {locationLines.slice(1).map((line, index) => (
                    <p key={`${line}-${index}`} className="text-base text-muted-foreground">{line}</p>
                  ))}
                </DetailIconRow>
              )}

              {dressCode && (
                <DetailIconRow icon={<Tag className="size-4 text-primary" />}>
                  <p className="text-base font-medium text-muted-foreground">Dress Code</p>
                  <p className="font-semibold">{dressCode}</p>
                </DetailIconRow>
              )}

              {showPurchaseCard && (
                <div className="rounded-xl border border-black/5 bg-card p-4">
                  {event.starting_ticket_price && (
                    <>
                      <p className="text-base font-medium text-muted-foreground">Starting at</p>
                      <p className="text-3xl font-semibold tabular-nums">{formatPrice(event.starting_ticket_price)}</p>
                    </>
                  )}
                  <PurchaseActions event={event} organization={organization} isPreview={isPreview} />
                </div>
              )}

              <SaveEventButton event={event} isSaved={isSaved} brandFree={brandFree} isPreview={isPreview} />

              <ShareAndCalendar event={event} brandFree={brandFree} eventUrl={eventUrl} isPreview={isPreview} />

              <OrganizationCard
                event={event}
                organization={organization}
                isFollowing={isFollowing}
                brandFree={brandFree}
                isPreview={isPreview}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
